import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AltalabLogo } from "../components/AltalabLogo";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Clock,
  Check,
  X,
  MessageCircle,
  ArrowLeft,
  Loader2,
  Search,
  RefreshCw,
} from "lucide-react";
import { Input } from "../components/ui/input";
import BottomNavigation from "../components/BottomNavigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConnectionsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("connections");
  const [connections, setConnections] = useState([]);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [connectionsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/connections?Authorization=Bearer ${token}`),
        axios.get(`${API}/connections/requests?Authorization=Bearer ${token}`)
      ]);
      setConnections(connectionsRes.data.connections || []);
      setReceived(requestsRes.data.received || []);
      setSent(requestsRes.data.sent || []);
    } catch (error) {
      console.error("Failed to fetch connections:", error);
      toast.error("Erro ao carregar conexões");
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (connectionId, accept) => {
    try {
      await axios.put(
        `${API}/connections/${connectionId}/respond?Authorization=Bearer ${token}`,
        { accept }
      );
      toast.success(accept ? "Conexão aceita!" : "Solicitação recusada");
      fetchData();
    } catch (error) {
      toast.error("Erro ao responder solicitação");
    }
  };

  const handleRemoveConnection = async (connectionId) => {
    try {
      await axios.delete(`${API}/connections/${connectionId}?Authorization=Bearer ${token}`);
      toast.success("Conexão removida");
      fetchData();
    } catch (error) {
      toast.error("Erro ao remover conexão");
    }
  };

  const getPhoto = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  const filteredConnections = connections.filter(conn => {
    if (!searchQuery) return true;
    const name = conn.user?.display_name?.toLowerCase() || "";
    const username = conn.user?.username?.toLowerCase() || "";
    return name.includes(searchQuery.toLowerCase()) || username.includes(searchQuery.toLowerCase());
  });

  const ConnectionCard = ({ connection, showActions = false, type = "connection" }) => {
    const user = type === "connection" ? connection.user : 
                 type === "received" ? connection.from_user : 
                 connection.to_user;
    
    if (!user) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
            {getPhoto(user.profile_photo) ? (
              <img src={getPhoto(user.profile_photo)} alt={user.display_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/30">
                <Users className="w-7 h-7" />
              </div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-white truncate">{user.display_name}</div>
            <div className="text-sm text-white/50">@{user.username}</div>
            
            {/* Platform stats */}
            {user.platform_stats && Object.keys(user.platform_stats).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(user.platform_stats).map(([platform, count]) => (
                  <Badge key={platform} variant="outline" className="text-xs border-white/20 text-white/60">
                    {platform}: {count >= 1000 ? `${(count/1000).toFixed(1)}k` : count}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {type === "connection" && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white/70 hover:text-white"
                  onClick={() => navigate(`/profile/${user.id}`)}
                >
                  Ver Perfil
                </Button>
                <Button
                  size="sm"
                  className="bg-[#FD5564] hover:bg-[#FF7854]"
                  onClick={() => navigate(`/exchanges/new/${user.id}`)}
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Trocar
                </Button>
              </>
            )}
            
            {type === "received" && (
              <>
                <Button
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600"
                  onClick={() => handleRespond(connection.id, true)}
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                  onClick={() => handleRespond(connection.id, false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            )}
            
            {type === "sent" && (
              <Badge variant="outline" className="border-yellow-500/50 text-yellow-400">
                <Clock className="w-3 h-3 mr-1" />
                Pendente
              </Badge>
            )}
          </div>
        </div>
        
        {connection.message && type !== "connection" && (
          <div className="mt-3 p-3 bg-white/5 rounded-lg text-sm text-white/70">
            "{connection.message}"
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/discovery")}
              className="text-white/70"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Conexões</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchData}
            className="text-white/70"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-white/5 border border-white/10 mb-6">
              <TabsTrigger value="connections" className="flex-1 data-[state=active]:bg-[#FD5564]">
                <UserCheck className="w-4 h-4 mr-2" />
                Conectados ({connections.length})
              </TabsTrigger>
              <TabsTrigger value="received" className="flex-1 data-[state=active]:bg-[#FD5564]">
                <UserPlus className="w-4 h-4 mr-2" />
                Recebidas ({received.length})
              </TabsTrigger>
              <TabsTrigger value="sent" className="flex-1 data-[state=active]:bg-[#FD5564]">
                <Clock className="w-4 h-4 mr-2" />
                Enviadas ({sent.length})
              </TabsTrigger>
            </TabsList>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
              </div>
            ) : (
              <>
                <TabsContent value="connections">
                  {/* Search */}
                  <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <Input
                      placeholder="Buscar conexões..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>

                  <div className="space-y-3">
                    <AnimatePresence>
                      {filteredConnections.length > 0 ? (
                        filteredConnections.map((conn) => (
                          <ConnectionCard key={conn.connection_id} connection={conn} type="connection" />
                        ))
                      ) : (
                        <div className="text-center py-16">
                          <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                          <p className="text-white/50 mb-4">Nenhuma conexão ainda</p>
                          <Button
                            className="bg-[#FD5564] hover:bg-[#FF7854]"
                            onClick={() => navigate("/discovery")}
                          >
                            Descobrir Criadores
                          </Button>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                </TabsContent>

                <TabsContent value="received">
                  <div className="space-y-3">
                    <AnimatePresence>
                      {received.length > 0 ? (
                        received.map((conn) => (
                          <ConnectionCard key={conn.id} connection={conn} type="received" />
                        ))
                      ) : (
                        <div className="text-center py-16">
                          <UserPlus className="w-12 h-12 text-white/20 mx-auto mb-4" />
                          <p className="text-white/50">Nenhuma solicitação pendente</p>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                </TabsContent>

                <TabsContent value="sent">
                  <div className="space-y-3">
                    <AnimatePresence>
                      {sent.length > 0 ? (
                        sent.map((conn) => (
                          <ConnectionCard key={conn.id} connection={conn} type="sent" />
                        ))
                      ) : (
                        <div className="text-center py-16">
                          <Clock className="w-12 h-12 text-white/20 mx-auto mb-4" />
                          <p className="text-white/50">Nenhuma solicitação enviada</p>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default ConnectionsPage;
